import type { GetRecommendationsData } from '@vizit/api';
import {
  Badge,
  BadgeLevel,
  BadgeShape,
  Caption,
  ScoreStickerV2,
  ScoreStickerV2Size,
  Skeleton,
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from '@vizit/atoms';
import { RETAILER_CONFIG_MAP } from '@vizit/constants';
import type { RetailerKey } from '@vizit/constants';
import { CategoryBreadcrumbs } from '../category-breadcrumbs';
import type { OptimizationRecommendationDataMap } from '../optimization-recommendation-card';
import { ScoreExplain, getFactorsFromRecommendations, getFactorsFromRecommendationsV2 } from '../score-explain';
import { ProductHeaderInfoItem } from './product-header.info-item';

type PendingState<T = string> = {
  value: T;
  isPending: boolean;
};

interface ProductHeaderProps {
  /**
   * The name of the product with pending state
   * @required
   */
  name: PendingState<string>;
  /**
   * The Vizit listing score of the product with pending state
   * @required
   */
  listingScore: PendingState<number>;
  /**
   * The score change (difference) of the product with pending state
   * @optional
   */
  scoreChange: PendingState<number>;
  /**
   * The retailer of the product
   * @optional
   */
  retailer: string;
  /**
   * The ASIN of the product
   * @optional
   */
  itemId: string;
  /**
   * The brand of the product with pending state
   * @optional
   */
  brand: PendingState<string>;
  /**
   * The category of the product with pending state
   * @optional
   */
  category: PendingState<string>;
  /**
   * The category breadcrumbs from L1 down to the leaf category, shown in a tooltip on the category value
   * @optional
   */
  categoryBreadcrumbs?: string[];
  /**
   * Whether the product is pre-launch
   * @default false
   * @optional
   */
  isPreLaunch: boolean;
  /**
   * Whether the data (other than the score) is still loading
   * @default false
   */
  isDataLoading?: boolean;
  /**
   * Optimization recommendations data for computing factor statuses
   * @optional
   */
  optimizations?: {
    recommendations: OptimizationRecommendationDataMap | null;
    recommendationsV2: GetRecommendationsData | null;
    isLoading: boolean;
    isV2Enabled: boolean;
    isPollingForUpdatesV2?: boolean;
  };
  /**
   * The hero image's `vizit_certified` flag, used by the V1 factor list
   * @optional
   */
  heroCertified: boolean | null;
}

function ProductHeader(props: ProductHeaderProps) {
  const {
    name,
    listingScore,
    scoreChange,
    retailer,
    itemId,
    brand,
    category,
    categoryBreadcrumbs,
    isPreLaunch = false,
    isDataLoading = false,
    optimizations,
    heroCertified,
  } = props;

  const retailerConfig = RETAILER_CONFIG_MAP[retailer as RetailerKey] ?? null;
  const categoryTooltip = categoryBreadcrumbs?.length ? (
    <CategoryBreadcrumbs levels={categoryBreadcrumbs} />
  ) : undefined;

  // Extract the scored payload from recommendationsV2, handling both 200 (with metrics) and 202 (processing) responses
  const scoredV2 =
    optimizations?.recommendationsV2 && 'metrics' in optimizations.recommendationsV2
      ? optimizations.recommendationsV2
      : undefined;

  const factors = optimizations?.isV2Enabled
    ? getFactorsFromRecommendationsV2(
        scoredV2?.metrics,
        scoredV2?.scoreExplainHeroCertified,
        scoredV2?.scoreExplainCarouselCertified,
      )
    : getFactorsFromRecommendations(optimizations?.recommendations, heroCertified);

  // Show loading when:
  // 1. Initially loading data (isLoading)
  // 2. Polling for updates after receiving 202 response (isPollingForUpdatesV2)
  const isFactorsLoading = optimizations?.isLoading || optimizations?.isPollingForUpdatesV2 || false;

  return (
    <Caption
      label="Product Page info"
      infoCustomComponent={
        <ScoreExplain
          score={listingScore.value}
          factors={factors}
          isScoreLoading={listingScore.isPending}
          isFactorsLoading={isFactorsLoading}
        />
      }
    >
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-row gap-12 flex-1 items-center justify-center min-h-[200px]">
          <ScoreStickerV2
            score={listingScore.value}
            size={ScoreStickerV2Size.Lg}
            isLoading={listingScore.isPending}
            // Pre-launch (staged) pages were never live, so there's no score at
            // ingest to diff against — `score_change` is meaningless here.
            scoreDiff={isPreLaunch || scoreChange.isPending ? null : scoreChange.value || null}
          />
          <div className="flex flex-col gap-12 flex-1">
            <div className="min-h-[60px] w-full">
              {name.isPending ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-12 w-full" variant="square" />
                  <Skeleton className="h-12 w-1/4" variant="square" />
                </div>
              ) : (
                <Text
                  elementType="h1"
                  size={TextSize.Lg}
                  color={TextColor.Secondary}
                  weight={TextWeight.Medium}
                  maxLines={3}
                >
                  {name.value || 'Unnamed product'}
                </Text>
              )}
            </div>
            <div className="grid grid-cols-4 gap-12 w-full md:w-3/4">
              {!isPreLaunch && (
                <>
                  <ProductHeaderInfoItem
                    label="Retailer"
                    value={retailerConfig?.displayName || retailer}
                    isLoading={isDataLoading}
                  />
                  <ProductHeaderInfoItem
                    label={retailerConfig?.itemIdLabel || 'ASIN'}
                    value={itemId}
                    isLoading={isDataLoading}
                  />
                </>
              )}

              <ProductHeaderInfoItem label="Brand" value={brand.value} isLoading={brand.isPending} />
              <ProductHeaderInfoItem
                label="Category"
                value={category.value}
                isLoading={category.isPending}
                tooltip={categoryTooltip}
              />
              {isPreLaunch && (
                <Badge className="w-max h-fit" level={BadgeLevel.Default} shape={BadgeShape.Box}>
                  Pre-launch
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </Caption>
  );
}

export { ProductHeader };
export type { ProductHeaderProps, PendingState };
